import React, { useRef } from "react";
import tailwindClassnames from "./TailwindClassnames";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./customComponentStyles/DateRangeSelector.css";
import { t } from "i18next";
import { useProfile } from "./ProfileProvider";
import {
  preSelectionFromTypedPrefix,
  preSelectionFromTypedYearMonth,
} from "./helpers";

type DateSelectorProps = {
  label: string;
  name: string;
  date: Date | null;
  onDateChange: any;
  excludeDatesInterval?: { start: Date; end: Date }[] | undefined;
  maxDate?: Date;
  disabled?: boolean;
};

const DateSelector: React.FC<DateSelectorProps> = ({
  label,
  name,
  date,
  onDateChange,
  excludeDatesInterval,
  maxDate,
  disabled = false,
}) => {
  const { theme } = useProfile();
  const isDarkMode = theme === "dark";
  const datePickerRef = useRef<DatePicker>(null);
  const rawTextRef = useRef("");

  // Calculate the appropriate className based on enabled/disabled state and theme
  const getInputClassName = () => {
    if (disabled) {
      return isDarkMode
        ? "rounded px-3 py-2 text-greyed-text bg-storm-blue w-full border-none"
        : "rounded px-3 py-2 text-greyed-text bg-off-white-dark w-full border-none";
    } else {
      return isDarkMode
        ? "rounded px-3 py-2 text-off-white-dark bg-storm-blue border border-off-black-light w-full"
        : "rounded px-3 py-2 text-primary-off-black bg-white border border-off-white-dark w-full";
    }
  };

  // Custom input that receives styling props and, now that it's typeable
  // (no more `readOnly`), forwards react-datepicker's injected onChange/
  // etc. via the prop spread rather than dropping them. `onKeyDown` is
  // chained (not replaced): react-datepicker's own injected handler
  // (Enter/Escape/Tab on the text field) still needs to run, so this only
  // adds the year/month snap-on-Enter behavior on top of it. This must
  // live here, not as a top-level `<DatePicker onKeyDown>` prop — that
  // slot feeds the *calendar popup's* day-grid keyboard navigation, not
  // the text field.
  const CustomInput = React.forwardRef<
    HTMLInputElement,
    React.InputHTMLAttributes<HTMLInputElement>
  >(({ onKeyDown: injectedOnKeyDown, ...rest }, ref) => (
    <input
      {...rest}
      ref={ref}
      disabled={disabled}
      className={getInputClassName()}
      placeholder={disabled ? "" : t("Select...")}
      onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
          const preSelection = preSelectionFromTypedYearMonth(
            rawTextRef.current,
          );
          if (preSelection)
            datePickerRef.current?.setPreSelection(preSelection);
        }
        injectedOnKeyDown?.(event);
      }}
    />
  ));

  /**
   * Live-typing feedback: navigate the open calendar as soon as a complete
   * year (4 digits) or month (2 digits, or 1-2 digits confirmed by a
   * trailing dash) is typed, without waiting for a full date — see
   * preSelectionFromTypedPrefix.
   */
  const handleChangeRaw = (
    event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
  ) => {
    const value = (event?.target as HTMLInputElement | undefined)?.value;
    if (value === undefined) return;
    rawTextRef.current = value;
    const preSelection = preSelectionFromTypedPrefix(value);
    if (preSelection) datePickerRef.current?.setPreSelection(preSelection);
  };

  // Label styling should also match disabled state
  const labelClassName = tailwindClassnames.textInputLabel;

  return (
    <div className={`flex flex-col ${isDarkMode ? "dark-mode" : ""}`}>
      <label className={labelClassName}>{label}</label>
      <DatePicker
        ref={datePickerRef}
        selected={date}
        onChange={(e) =>
          onDateChange({
            target: {
              name: name,
              value: e,
            },
          })
        }
        onChangeRaw={handleChangeRaw}
        className={getInputClassName()}
        dateFormat="yyyy-MM-dd"
        // See DateRangeSelector.tsx for why this is required — without it,
        // partial typed input (e.g. "1") can silently resolve to a bogus
        // real date via react-datepicker's native-Date parsing fallback.
        strictParsing
        todayButton={t("Today")}
        showMonthDropdown
        showYearDropdown
        dropdownMode="select"
        excludeDateIntervals={excludeDatesInterval}
        maxDate={maxDate}
        disabled={disabled}
        calendarClassName={`custom-calendar ${
          isDarkMode ? "dark-mode-calendar" : ""
        }`}
        dayClassName={(day) =>
          date && day && day.getTime() === date.getTime() ? "selected-day" : ""
        }
        customInput={
          <CustomInput disabled={disabled} className={getInputClassName()} />
        }
      />
    </div>
  );
};

export default DateSelector;
